import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import formatTimestamp from '../../../i18n/formatTimestamp';

import './AssetDetailsPane.scss';
import { Icon, Tooltip } from '@salesforce/design-system-react';
import { FlagContext } from '../../../flags/Flags';

class AssetDetailsPane extends Component {
  constructor(props) {
    super(props);
    this.state = {
      detailsExpanded: false
    };
  }

  toggleExpand() {
    this.setState({ detailsExpanded: !this.state.detailsExpanded });
  }

  getCachedSelectionAttributes() {
    const keys = [...this.props.attributes.map(attr => attr.name)];
    const custom = this.props.selection?.asset?.custom || [];
    custom.forEach(attr => keys.push(attr.tagType));

    const attributes = {};
    new Set(keys).forEach(key => {
      attributes[key] =
        custom.find(attr => attr.tagType === key)?.tagValue || null;
    });

    return attributes;
  }

  getAssetAttributes() {
    const keys = [...this.props.attributes.map(attr => attr.name)];
    const custom = this.props.asset.custom || {};
    Object.keys(custom).forEach(key => keys.push(key));

    const attributes = {};
    new Set(keys).forEach(key => {
      attributes[key] = custom[key] || null;
    });
    return attributes;
  }

  renderSelectedArmDetails(flags) {
    if (
      !this.props.selection?.asset ||
      this.props.selection?.asset?.key !== this.props.asset?.key
    ) {
      return null;
    }
    const selectedAt = formatTimestamp(this.props.intl)(
      this.props.selection.timestamp
    );
    const icon = this.state.detailsExpanded ? 'chevrondown' : 'chevronright';

    let timeOfSelectionAttributes;
    let timeOfSelectionFields;
    let differencesNotice;
    if (this.state.detailsExpanded) {
      const attributes = this.getCachedSelectionAttributes();
      const currentAttributes = this.getAssetAttributes();

      const selectionAttributeDifferences = Object.keys(attributes).filter(
        key =>
          JSON.stringify(attributes[key]) !==
          JSON.stringify(currentAttributes[key])
      );
      const currentAttributeDifferences = Object.keys(currentAttributes).filter(
        key =>
          JSON.stringify(attributes[key]) !==
          JSON.stringify(currentAttributes[key])
      );

      const combinedAttributes = [
        ...selectionAttributeDifferences,
        currentAttributeDifferences
      ];
      const attributeDifferences = [...new Set(combinedAttributes)];

      // render the attributes
      timeOfSelectionAttributes = (
        <React.Fragment>
          <div className="asset-details-section-header asset-details-inner">
            <FormattedMessage id="components.ecs.selectionBrowser.assetDetails.attributes" />
          </div>
          {this.renderAttributeValues(
            attributes,
            'at-selection',
            attributeDifferences
          )}
        </React.Fragment>
      );

      const fieldDifferences = [];
      const standardFields = this.standardFields(flags);
      standardFields.forEach(field => {
        if (this.props.selection.asset[field] !== this.props.asset[field]) {
          fieldDifferences.push(field);
        }
      });

      timeOfSelectionFields = (
        <React.Fragment>
          <div className="asset-details-section-header asset-details-inner">
            <FormattedMessage id="components.ecs.selectionBrowser.assetDetails.standardFields" />
          </div>
          {this.renderStandardFields(
            flags,
            this.props.selection.asset,
            'at-selection',
            fieldDifferences
          )}
        </React.Fragment>
      );

      if (attributeDifferences.length || fieldDifferences.length) {
        differencesNotice = (
          <div className="notice">
            <FormattedMessage id="components.ecs.selectionBrowser.assetDetails.notice" />
          </div>
        );
      }
    }

    return (
      <React.Fragment>
        <div className="asset-details-inner">
          <div className="asset-details-section-header">
            <FormattedMessage id="components.ecs.selectionBrowser.assetDetails.selectedAt" />
          </div>
          <div>
            <span>{selectedAt}</span>
          </div>
        </div>
        <div className="asset-details-divider" />
        <div className="asset-details-inner time-of-selection">
          <div
            className="asset-details-accordion-header clickable"
            onClick={() => this.toggleExpand()}
          >
            <Icon category="utility" name={icon} size="x-small" />
            <FormattedMessage id="components.ecs.selectionBrowser.assetDetails.detailsAtSelectionTime" />
          </div>

          {timeOfSelectionFields}
          {timeOfSelectionAttributes}
          {differencesNotice}
        </div>
      </React.Fragment>
    );
  }

  renderAttributeValues(attributes, key, differences) {
    const tags = Object.entries(attributes).map(([key, value]) => ({
      key,
      value: Array.isArray(value) ? value.join(', ') : value
    }));
    tags.sort((a, b) => a.key.localeCompare(b.key));
    const asteriskColumn = key => (
      <span className="asterisk-column">
        {differences?.includes(key) ? '*' : ''}
      </span>
    );
    return (
      <div className="asset-details-tag-list asset-details-inner">
        {tags.map((tag, idx) => (
          <React.Fragment key={`asset-attribute-tag-row-${key}-${idx}`}>
            {asteriskColumn(tag.key)}
            <div className="tag-item">
              <span className="tag-key">{tag.key}</span>
            </div>
            <div className="tag-item">
              <span className="tag-value">{tag.value}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  }

  standardFields(flags) {
    const fields = [
      'start_datetime',
      'end_datetime',
      'fallback',
      'availability'
    ];
    if (flags.subjectLine) {
      fields.push('subject_line');
      fields.push('preheader');
    }
    return fields;
  }

  renderStandardFields(flags, asset, key, differences) {
    const { formatMessage } = this.props.intl;

    const fields = this.standardFields(flags);
    const tags = fields.map(field => {
      let value = asset[field];
      if (field.endsWith('datetime')) {
        value = formatTimestamp(this.props.intl)(value);
      }
      return {
        field,
        key: formatMessage({ id: `components.ecs.assetBrowser.${field}` }),
        value
      };
    });
    const asteriskColumn = key => (
      <span className="asterisk-column">
        {differences?.includes(key) ? '*' : ''}
      </span>
    );
    return (
      <div className="asset-details-tag-list asset-details-inner">
        {tags.map((tag, idx) => (
          <React.Fragment key={`standard-field-tag-row-${key}-${idx}`}>
            {asteriskColumn(tag.field)}
            <div className="tag-item">
              <span className="tag-key">{tag.key}</span>
            </div>
            <div className="tag-item">
              <span className="tag-value">{tag.value}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  }

  render() {
    const { formatMessage } = this.props.intl;
    if (!this.props.asset) {
      return null;
    }

    const tooltipContent = formatMessage({
      id: 'components.ecs.selectionBrowser.assetDetails.tooltip'
    });

    return (
      <FlagContext.Consumer>
        {flags => (
          <div className="asset-details-pane">
            <div className="asset-details-header-cell">
              <FormattedMessage id="components.ecs.selectionBrowser.assetDetails.header" />
              <Tooltip
                position="overflowBoundaryElement"
                align="top right"
                content={tooltipContent}
                variant="learnMore"
              />
            </div>
            <div className="asset-details-preview">
              <img
                src={this.props.asset.image_link}
                alt={this.props.asset.key}
              />
            </div>
            <div className="asset-details-section-header asset-details-inner">
              <FormattedMessage id="components.ecs.selectionBrowser.assetDetails.standardFields" />
            </div>

            {this.renderStandardFields(flags, this.props.asset, 'default')}
            <div className="asset-details-section-header asset-details-inner">
              <FormattedMessage id="components.ecs.selectionBrowser.assetDetails.attributes" />
            </div>
            {this.renderAttributeValues(this.getAssetAttributes(), 'default')}
            {this.renderSelectedArmDetails(flags)}
          </div>
        )}
      </FlagContext.Consumer>
    );
  }
}

AssetDetailsPane.contextTypes = {
  router: PropTypes.object
};

AssetDetailsPane.propTypes = {
  actions: PropTypes.object,
  asset: PropTypes.shape({
    key: PropTypes.string,
    image_link: PropTypes.string,
    custom: PropTypes.object
  }),
  attributes: PropTypes.array,
  selection: PropTypes.object,
  location: PropTypes.object
};

export default injectIntl(AssetDetailsPane);
